import { ArrowLeft, ArrowRight } from '@oriola-corporation/kronan-icons';
import React from 'react';
import { useTheme } from '../../theme';
import { LinkProps as ThemeLinkProps } from '../../theme/ThemeContext';
import { cn } from '../../utils';

export type LinkButtonVariant = 'primary' | 'secondary';
export type LinkButtonSize = 'default' | 'small';

export type LinkButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: LinkButtonVariant;
  size?: LinkButtonSize;
  isBackLink?: boolean;
} & ThemeLinkProps;

const LinkButtonComponent = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    { variant = 'primary', size = 'default', className, children, isBackLink = false, ...props },
    ref
  ) => {
    // This allows users to override the standard a-tag with a custom link component by setting it in the ThemeProvider.
    // E.g in gatsby-pages we use Gatsbys Link component instead of the standard a-tag.
    const { LinkComponent: ThemedLinkComponent } = useTheme();

    return (
      <ThemedLinkComponent
        ref={ref}
        className={cn(
          'ripple inline-flex items-center justify-center gap-1 rounded-full text-base font-medium',
          'min-w-[100px]',
          'cursor-pointer border-none',
          size === 'default' && 'px-5 py-2',
          size === 'small' && 'px-4 py-1',
          variant === 'primary' && 'bg-primary text-white hover:bg-green-800',
          variant === 'secondary' &&
            'bg-white text-primary ring-2 ring-inset ring-primary hover:bg-green-50 focus-visible:ring-transparent',
          className
        )}
        {...props}
      >
        {isBackLink && <ArrowLeft className="text-2xl" />}
        {children}
        {!isBackLink && <ArrowRight className="text-2xl" />}
      </ThemedLinkComponent>
    );
  }
);

LinkButtonComponent.displayName = 'LinkButton';

/**
 * A link component styled as a button with an arrow icon.
 *
 * @param {LinkButtonProps} props - LinkButton component props
 * @param {LinkButtonVariant} [props.variant] - Visual style variant ('primary' or 'secondary')
 * @param {LinkButtonSize} [props.size] - Button size ('default' or 'small')
 * @param {React.Ref<HTMLAnchorElement>} ref - Forwarded ref to the underlying anchor element
 */
export const LinkButton = LinkButtonComponent;
